import {
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { X } from '@kula-ai/icons/outline/X';
import { KButton } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import type { WorkflowRule } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import type { StoppingAction } from './deriveCurrentStageStopping';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';
import { AutomationPreviewBody } from './AutomationPreviewBody';

const STR = MOVE_WITH_AUTOMATION_STRINGS;

export type MoveWithAutomationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  targetStageName: string;
  targetRule: WorkflowRule | null;
  currentStageName?: string;
  currentStageStoppingActions?: StoppingAction[];
  onCancel: () => void;
  onMoveWithoutAutomation: () => void;
  onMove: () => void;
  isMoving?: boolean;
  dataTestId?: string;
};

export const MoveWithAutomationModal = ({
  isOpen,
  onClose,
  targetStageName,
  targetRule,
  currentStageName,
  currentStageStoppingActions = [],
  onCancel,
  onMoveWithoutAutomation,
  onMove,
  isMoving = false,
  dataTestId = 'automation-preview-move-modal',
}: MoveWithAutomationModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
    <ModalOverlay />
    <ModalContent
      borderRadius="lg"
      overflow="hidden"
      data-test-id="automation-move-modal"
    >
      <ModalHeader pb="0">
        <Flex justify="space-between" align="start" gap="12px">
          <Flex direction="column" gap="4px">
            <KText size="md-semi-bold" color="secondary-900">
              {STR.modal.title}
            </KText>
            <KText size="sm-regular" color="secondary-600">
              {STR.modal.body}
            </KText>
          </Flex>
          <Icon
            as={X}
            w="20px"
            h="20px"
            cursor="pointer"
            color="secondary-600"
            onClick={onClose}
            aria-label={STR.modal.closeAriaLabel}
          />
        </Flex>
      </ModalHeader>

      <ModalBody pt="16px">
        <AutomationPreviewBody
          targetStageName={targetStageName}
          targetRule={targetRule}
          currentStageName={currentStageName}
          currentStageStoppingActions={currentStageStoppingActions}
          dataTestId={dataTestId}
        />
      </ModalBody>

      <ModalFooter gap="8px">
        <KButton
          variant="secondary"
          size="md"
          onClick={onCancel}
          isDisabled={isMoving}
        >
          {STR.cta.cancel}
        </KButton>
        <KButton
          variant="tertiary"
          size="md"
          onClick={onMoveWithoutAutomation}
          isDisabled={isMoving}
        >
          {STR.cta.moveWithoutAutomation}
        </KButton>
        <KButton
          variant="primary"
          size="md"
          onClick={onMove}
          isLoading={isMoving}
          data-test-id="automation-move-confirm"
        >
          {STR.cta.move}
        </KButton>
      </ModalFooter>
    </ModalContent>
  </Modal>
);
